'use client';
import { useState } from 'react';
import Header from '@/components/common/Header';
import Footer from '@/components/common/Footer';
import Link from '@/components/ui/Link';

interface FormData {
    name: string;
    email: string;
    phone: string;
    subject: string;
    message: string;
}

interface FaqItem {
    question: string;
    answer: string;
}

const faqs: FaqItem[] = [
    {
        question: 'How long does delivery take for my order?',
        answer: 'Standard delivery takes 3–5 business days across India. Express shipping (1–2 days) is available at checkout for eligible pin codes. International orders usually arrive in 7–14 business days.',
    },
    {
        question: 'What is your return and exchange policy?',
        answer: 'We offer a 30-day hassle-free return and exchange policy on all unworn, unwashed items with original tags intact. Pickup is arranged right from your doorstep.',
    },
    {
        question: 'How can I track the status of my order?',
        answer: 'Once your order is dispatched, you will receive an automated tracking link via email and WhatsApp. You can also track live updates on our Track Order page in the navigation menu.',
    },
    {
        question: 'Do you offer international shipping?',
        answer: 'Yes! We ship to over 14 countries worldwide. Customs and import duties, if applicable, are calculated at checkout for a seamless delivery experience.',
    },
    {
        question: 'What payment methods do you accept?',
        answer: 'We accept all major Credit/Debit Cards, Net Banking, UPI (Google Pay, PhonePe, Paytm), Apple Pay, PayPal, and Cash on Delivery (COD) for domestic orders.',
    },
];

const contactCards = [
    {
        icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
            </svg>
        ),
        label: 'VISIT OUR STORE',
        title: 'Surat Experience Store',
        value: '14 Fashion Street, Koregaon Park',
        sub: 'Surat, Gujarat 395010',
    },
    {
        icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
            </svg>
        ),
        label: 'CALL OR WHATSAPP',
        title: 'Direct Customer Helpline',
        value: '+91 63542 45275',
        sub: 'Mon – Sat, 9:00 AM – 7:00 PM IST',
    },
    {
        icon: (
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
            </svg>
        ),
        label: 'EMAIL US',
        title: 'Customer Care & Sales',
        value: 'hello@nehdowellness.com',
        sub: 'We respond within 24 hours',
    },
];

export default function ContactPage() {
    const [form, setForm] = useState<FormData>({ name: '', email: '', phone: '', subject: '', message: '' });
    const [submitted, setSubmitted] = useState(false);
    const [openFaq, setOpenFaq] = useState<number | null>(0);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
        setForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitted(true);
    };

    return (
        <div className="min-h-screen bg-background-tertiary flex flex-col">
            <Header />

            <main className="flex-1 w-full">
                {/* Hero Banner */}
                <section className="w-full bg-background-secondary py-10 sm:py-14 lg:py-[60px]">
                    <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px]">
                        <div className="flex flex-col gap-2 max-w-3xl">
                            <div className="flex items-center gap-2 text-sm text-text-opacity-60">
                                <Link href="/" ariaLabel="Home" className="hover:text-primary-brand transition-colors">Home</Link>
                                <span>/</span>
                                <span className="text-text-primary font-medium">Contact Us</span>
                            </div>
                            <h1 className="text-4xl sm:text-5xl lg:text-7xl text-text-primary font-semibold leading-tight">
                                Contact Us
                            </h1>
                            <p className="text-lg sm:text-xl text-text-opacity-60 font-normal mt-1">
                                We'd love to hear from you. Have a question, feedback, or need assistance? Reach out to our team.
                            </p>
                        </div>
                    </div>
                </section>

                {/* 3 Contact Info Cards */}
                <section className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px] py-10 sm:py-12 lg:py-14">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-5 lg:gap-6">
                        {contactCards.map((card, idx) => (
                            <div
                                key={idx}
                                className="bg-white rounded-2xl p-5 sm:p-7 border border-border-light shadow-sm hover:shadow-md transition-all duration-300 flex flex-col gap-4 min-w-0"
                            >
                                <div className="w-12 h-12 rounded-xl bg-primary-brand/10 text-primary-brand flex items-center justify-center flex-shrink-0">
                                    {card.icon}
                                </div>
                                <div className="flex flex-col gap-1 min-w-0">
                                    <span className="text-xs font-bold text-primary-brand tracking-widest uppercase">{card.label}</span>
                                    <h3 className="text-base sm:text-lg font-semibold text-text-primary">{card.title}</h3>
                                    <p className="text-sm sm:text-base font-medium text-text-primary mt-0.5 break-all sm:break-words">{card.value}</p>
                                    <p className="text-xs sm:text-sm text-text-opacity-60">{card.sub}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </section>

                {/* Main 2-Column Section: Form + Store Details (Height Equalized) */}
                <section className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px] pb-12 sm:pb-16 lg:pb-20">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-stretch">

                        {/* Left: Contact Form (7 cols) */}
                        <div className="lg:col-span-7 bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 lg:p-10 border border-border-light shadow-sm flex flex-col justify-between h-full">
                            <div>
                                <div className="mb-6 sm:mb-8">
                                    <h2 className="text-2xl sm:text-3xl font-bold text-text-primary">Send Us a Message</h2>
                                    <p className="text-text-opacity-60 text-sm sm:text-base mt-1">
                                        Fill out the form below and our customer support team will respond within 24 hours.
                                    </p>
                                </div>

                                {submitted ? (
                                    <div className="py-12 text-center flex flex-col items-center gap-4">
                                        <div className="w-16 h-16 rounded-full bg-emerald-50 text-emerald-600 flex items-center justify-center">
                                            <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M5 13l4 4L19 7" />
                                            </svg>
                                        </div>
                                        <h3 className="text-2xl font-bold text-text-primary">Thank You!</h3>
                                        <p className="text-text-opacity-60 text-base max-w-md">
                                            Your message has been received. Our team will get back to you shortly at <span className="font-semibold text-text-primary">{form.email}</span>.
                                        </p>
                                        <button
                                            onClick={() => { setSubmitted(false); setForm({ name: '', email: '', phone: '', subject: '', message: '' }); }}
                                            className="mt-4 px-6 py-2.5 bg-primary-brand text-white text-sm font-semibold rounded-full hover:bg-primary-brand-dark transition-colors"
                                        >
                                            Send Another Message
                                        </button>
                                    </div>
                                ) : (
                                    <form onSubmit={handleSubmit} className="flex flex-col gap-4 sm:gap-5">
                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                                            <div className="flex flex-col gap-1.5">
                                                <label htmlFor="name" className="text-sm font-semibold text-text-primary">Full Name *</label>
                                                <input
                                                    id="name"
                                                    name="name"
                                                    type="text"
                                                    required
                                                    value={form.name}
                                                    onChange={handleChange}
                                                    placeholder="Sidhesh kathrotiya"
                                                    className="w-full px-4 py-3 rounded-xl border border-border-light bg-background-tertiary text-text-primary text-sm focus:outline-none focus:border-primary-brand focus:ring-1 focus:ring-primary-brand transition-all"
                                                />
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <label htmlFor="email" className="text-sm font-semibold text-text-primary">Email Address *</label>
                                                <input
                                                    id="email"
                                                    name="email"
                                                    type="email"
                                                    required
                                                    value={form.email}
                                                    onChange={handleChange}
                                                    placeholder="arjun@example.com"
                                                    className="w-full px-4 py-3 rounded-xl border border-border-light bg-background-tertiary text-text-primary text-sm focus:outline-none focus:border-primary-brand focus:ring-1 focus:ring-primary-brand transition-all"
                                                />
                                            </div>
                                        </div>

                                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
                                            <div className="flex flex-col gap-1.5">
                                                <label htmlFor="phone" className="text-sm font-semibold text-text-primary">Phone Number</label>
                                                <input
                                                    id="phone"
                                                    name="phone"
                                                    type="tel"
                                                    value={form.phone}
                                                    onChange={handleChange}
                                                    placeholder="+91 98765 43210"
                                                    className="w-full px-4 py-3 rounded-xl border border-border-light bg-background-tertiary text-text-primary text-sm focus:outline-none focus:border-primary-brand focus:ring-1 focus:ring-primary-brand transition-all"
                                                />
                                            </div>

                                            <div className="flex flex-col gap-1.5">
                                                <label htmlFor="subject" className="text-sm font-semibold text-text-primary">Subject *</label>
                                                <select
                                                    id="subject"
                                                    name="subject"
                                                    required
                                                    value={form.subject}
                                                    onChange={handleChange}
                                                    className="w-full px-4 py-3 rounded-xl border border-border-light bg-background-tertiary text-text-primary text-sm focus:outline-none focus:border-primary-brand focus:ring-1 focus:ring-primary-brand transition-all cursor-pointer"
                                                >
                                                    <option value="" disabled>Select a topic</option>
                                                    <option value="Order Enquiry">Order & Delivery</option>
                                                    <option value="Returns & Exchanges">Returns & Exchanges</option>
                                                    <option value="Product Assistance">Product Assistance</option>
                                                    <option value="Wholesale">Wholesale & Business</option>
                                                    <option value="Feedback">Feedback / Other</option>
                                                </select>
                                            </div>
                                        </div>

                                        <div className="flex flex-col gap-1.5">
                                            <label htmlFor="message" className="text-sm font-semibold text-text-primary">Message *</label>
                                            <textarea
                                                id="message"
                                                name="message"
                                                required
                                                rows={5}
                                                value={form.message}
                                                onChange={handleChange}
                                                placeholder="Write your message here..."
                                                className="w-full px-4 py-3 rounded-xl border border-border-light bg-background-tertiary text-text-primary text-sm focus:outline-none focus:border-primary-brand focus:ring-1 focus:ring-primary-brand transition-all resize-none"
                                            />
                                        </div>

                                        <button
                                            type="submit"
                                            className="mt-2 w-full py-3.5 px-6 bg-primary-brand text-white font-semibold text-base rounded-xl hover:bg-primary-brand-dark active:scale-[0.99] transition-all duration-200 shadow-sm cursor-pointer"
                                        >
                                            Send Message →
                                        </button>
                                    </form>
                                )}
                            </div>
                        </div>

                        {/* Right: Store Info & Operating Hours (5 cols - Equalized Height) */}
                        <div className="lg:col-span-5 flex flex-col justify-between gap-6 h-full">
                            {/* Store Hours Card */}
                            <div className="bg-primary-brand text-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 shadow-sm flex-1 flex flex-col justify-between">
                                <div>
                                    <span className="text-xs font-bold tracking-widest uppercase text-primary-accent">STORE & SUPPORT HOURS</span>
                                    <h3 className="text-2xl font-bold mt-1 mb-4">When We're Open</h3>
                                </div>
                                <div className="flex flex-col gap-3">
                                    {[
                                        { day: 'Monday – Friday', hours: '9:00 AM – 8:00 PM IST' },
                                        { day: 'Saturday', hours: '10:00 AM – 7:00 PM IST' },
                                        { day: 'Sunday', hours: '11:00 AM – 5:00 PM IST' },
                                    ].map((item, i) => (
                                        <div key={i} className="flex justify-between items-center text-sm sm:text-base border-b border-white/10 pb-2.5 last:border-b-0 last:pb-0">
                                            <span className="text-white/80 font-medium">{item.day}</span>
                                            <span className="font-semibold text-white">{item.hours}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Location Preview Card */}
                            <div className="bg-white rounded-2xl sm:rounded-3xl p-6 sm:p-8 border border-border-light shadow-sm flex-1 flex flex-col justify-between gap-4">
                                <div>
                                    <span className="text-xs font-bold text-primary-brand tracking-widest uppercase">FLAGSHIP STORE</span>
                                    <h3 className="text-xl font-bold text-text-primary mt-0.5">NEHDO Wellness Surat</h3>
                                    <p className="text-sm sm:text-base text-text-opacity-60 leading-relaxed mt-2">
                                        Visit our concept studio to explore our full seasonal collection, meet our stylists, and experience the fabrics in person.
                                    </p>
                                </div>
                                <div className="p-4 bg-background-tertiary rounded-xl border border-border-light/60 flex items-start gap-3">
                                    <svg className="w-5 h-5 text-primary-brand flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                    </svg>
                                    <div className="text-sm text-text-primary font-medium">
                                        14 Fashion Street, Koregaon Park, Surat, GJ 395010
                                    </div>
                                </div>
                            </div>
                        </div>

                    </div>
                </section>

                {/* FAQ Section (Full Width Accordion) */}
                <section className="w-full bg-white py-16 sm:py-20 lg:py-24 border-t border-border-light">
                    <div className="w-full max-w-[1920px] mx-auto px-4 sm:px-6 lg:px-[60px]">
                        <div className="max-w-4xl mx-auto flex flex-col gap-8 sm:gap-10">
                            {/* FAQ Header */}
                            <div className="text-center flex flex-col gap-2">
                                <span className="text-xs font-bold text-primary-brand tracking-widest uppercase">NEED HELP?</span>
                                <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-text-primary">
                                    Frequently Asked Questions
                                </h2>
                                <p className="text-base sm:text-lg text-text-opacity-60 max-w-xl mx-auto">
                                    Find quick answers to common questions about orders, shipping, returns, and products.
                                </p>
                            </div>

                            {/* Accordion List */}
                            <div className="flex flex-col gap-3 sm:gap-4">
                                {faqs.map((faq, idx) => {
                                    const isOpen = openFaq === idx;
                                    return (
                                        <div
                                            key={idx}
                                            className={`rounded-2xl border transition-all duration-300 overflow-hidden ${isOpen
                                                ? 'bg-white border-primary-brand shadow-md'
                                                : 'bg-background-tertiary border-border-light/80 hover:border-primary-brand/50'
                                                }`}
                                        >
                                            <button
                                                onClick={() => setOpenFaq(isOpen ? null : idx)}
                                                className="w-full flex items-center justify-between p-5 sm:p-6 text-left gap-4 cursor-pointer"
                                                aria-expanded={isOpen}
                                            >
                                                <span className="text-base sm:text-lg font-semibold text-text-primary">{faq.question}</span>
                                                <span className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-300 ${isOpen
                                                    ? 'bg-primary-brand text-white border border-primary-brand rotate-180'
                                                    : 'bg-white text-text-primary border border-border-light'
                                                    }`}>
                                                    <svg className="w-4 h-4" fill="none" stroke="currentColor" strokeWidth="2.5" viewBox="0 0 24 24">
                                                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
                                                    </svg>
                                                </span>
                                            </button>
                                            {isOpen && (
                                                <div className="px-5 sm:px-6 pb-6 text-text-opacity-60 text-sm sm:text-base leading-relaxed border-t border-border-light/40 pt-4">
                                                    {faq.answer}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </section>
            </main>

            <Footer />
        </div>
    );
}